import React, { useState } from 'react';
import { Icon } from '@/shared/icons';
import type { PreconditionRule } from '@troia-v3/types';

// ─── Labels e resumo ────────────────────────────────────────────────────────

const RULE_TYPE_LABELS: Record<PreconditionRule['type'], string> = {
  questions_answered: 'Perguntas respondidas',
  context_check: 'Verificação de contexto',
  tag_present: 'Tag presente',
  field_value: 'Valor de campo',
  time_window: 'Janela de tempo',
};

const DAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function getRuleSummary(rule: PreconditionRule): string {
  switch (rule.type) {
    case 'questions_answered':
      return rule.questions.length > 0
        ? rule.questions.join(', ')
        : '(sem perguntas)';
    case 'context_check':
      return rule.question || '(sem pergunta)';
    case 'tag_present':
      return rule.tag || '(sem tag)';
    case 'field_value': {
      const opLabel: Record<string, string> = {
        '=': '=',
        '!=': '≠',
        '>': '>',
        '<': '<',
        contains: 'contém',
      };
      return `${rule.field} ${opLabel[rule.operator] ?? rule.operator} "${rule.value}"`;
    }
    case 'time_window': {
      const days =
        rule.allowedDays && rule.allowedDays.length > 0
          ? rule.allowedDays.map((d: number) => DAY_LABELS[d] ?? String(d)).join(', ')
          : 'Todos os dias';
      return `${days} · ${rule.startHour}h–${rule.endHour}h`;
    }
    default:
      return '';
  }
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface PreconditionRuleCardProps {
  rule: PreconditionRule;
  onEdit: () => void;
  onDelete: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const PreconditionRuleCard: React.FC<PreconditionRuleCardProps> = ({
  rule,
  onEdit,
  onDelete,
}) => {
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <div
      className="flex items-start gap-3 px-4 py-3 rounded-lg border"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
    >
      {/* Ícone de tipo */}
      <div
        className="p-1.5 rounded-md flex-shrink-0 mt-0.5"
        style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
      >
        <Icon name="filter" className="w-3.5 h-3.5" />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold mb-0.5" style={{ color: 'var(--theme-card-text)' }}>
          {RULE_TYPE_LABELS[rule.type]}
        </p>
        <p
          className="text-xs truncate"
          style={{ color: 'var(--theme-muted-text)' }}
          title={getRuleSummary(rule)}
        >
          {getRuleSummary(rule)}
        </p>
      </div>

      {/* Ações */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {confirmDelete ? (
          <>
            <span className="text-xs mr-1" style={{ color: '#ef4444' }}>
              Remover?
            </span>
            <button
              onClick={() => onDelete()}
              className="px-2 py-1 rounded text-xs font-medium"
              style={{ backgroundColor: '#ef4444', color: '#ffffff' }}
            >
              Sim
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              className="px-2 py-1 rounded text-xs font-medium"
              style={{ backgroundColor: 'var(--theme-card-border)', color: 'var(--theme-card-text)' }}
            >
              Não
            </button>
          </>
        ) : (
          <>
            <button
              onClick={onEdit}
              className="p-1.5 rounded-lg transition-colors"
              style={{ color: 'var(--theme-muted-text)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                e.currentTarget.style.color = 'var(--theme-card-text)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--theme-muted-text)';
              }}
              title="Editar regra"
            >
              <Icon name="edit" className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setConfirmDelete(true)}
              className="p-1.5 rounded-lg transition-colors"
              style={{ color: 'var(--theme-muted-text)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#fef2f2';
                e.currentTarget.style.color = '#ef4444';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--theme-muted-text)';
              }}
              title="Remover regra"
            >
              <Icon name="trash" className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};
